import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, User, Calendar, TrendingUp, BarChart3, Brain, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../../styles/blog.css';

const BlogPost1 = () => {
  return (
    <div className="blog-post-page">
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="blog-hero-content"
          >
            <Link to="/resources" className="back-link">
              <ArrowLeft size={20} />
              Back to Resources
            </Link>
            
            <div className="blog-category">Business Intelligence</div>
            
            <h1 className="blog-title">
              Reporting vs. Business Intelligence vs. Analytics: Which One Does Your Business Need?
            </h1>
            
            <div className="blog-meta">
              <span className="meta-item">
                <User size={16} />
                Abhishek Nishis
              </span>
              <span className="meta-item">
                <Calendar size={16} />
                December 15, 2024
              </span>
              <span className="meta-item">
                <Clock size={16} />
                8 min read
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="blog-content-section">
        <div className="container">
          <div className="blog-content">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {/* Introduction */}
              <div className="blog-intro">
                <p className="lead-text">
                  In today's data-driven landscape, organizations are constantly looking for ways to turn raw data into actionable insights. 
                  But with so many terms like reporting, business intelligence (BI), and analytics being thrown around, it's easy to get confused.
                </p>
                <p>
                  Are they the same? If not, how do they differ? More importantly, which one does your business actually need?
                </p>
                <p className="highlight-text">
                  While reporting focuses on structured, predefined data presentation, BI offers interactive insights, 
                  and analytics goes a step further with AI-driven forecasting and decision-making.
                </p>
              </div>

              {/* Reporting Section */}
              <div className="blog-section">
                <h2><BarChart3 className="section-icon" /> Reporting: The Foundation of Data Presentation</h2>
                
                <h3>What is Reporting?</h3>
                <p>
                  Reporting is about organizing raw data into structured, formatted reports that provide a clear snapshot 
                  of historical and current business performance. These reports are often static, scheduled, and essential 
                  for compliance, audits, and financial tracking.
                </p>

                <div className="feature-card">
                  <h4>Key Characteristics:</h4>
                  <ul className="feature-list">
                    <li><CheckCircle size={18} /> <strong>Predefined templates</strong> – Reports follow a structured format</li>
                    <li><CheckCircle size={18} /> <strong>Accurate, pixel-perfect layout</strong> – Crucial for legal and financial documents</li>
                    <li><CheckCircle size={18} /> <strong>Historical data focus</strong> – Provides what has already happened</li>
                    <li><CheckCircle size={18} /> <strong>Automated scheduling</strong> – Can be generated daily, weekly, or monthly</li>
                  </ul>
                </div>

                <div className="tools-grid">
                  <h4>Popular Reporting Tools:</h4>
                  <div className="tool-cards">
                    <div className="tool-card">
                      <strong>JasperReports</strong>
                      <p>Open-source reporting with highly customizable formats</p>
                    </div>
                    <div className="tool-card">
                      <strong>SSRS</strong>
                      <p>Microsoft's enterprise reporting solution</p>
                    </div>
                    <div className="tool-card">
                      <strong>Crystal Reports</strong>
                      <p>Used for detailed, pixel-perfect reporting</p>
                    </div>
                    <div className="tool-card">
                      <strong>Oracle Reports</strong>
                      <p>Forms discontinued, Publisher is cloud-only going forward</p>
                    </div>
                  </div>
                </div>

                <div className="use-case-box">
                  <h4>When to Use Reporting?</h4>
                  <ul>
                    <li>Regulatory & Financial Reporting – Tax reports, balance sheets, and compliance reports</li>
                    <li>Operational Reports – Sales summaries, inventory levels, production line reporting</li>
                    <li>Audit & Compliance Documentation – Ensuring transparency and accountability</li>
                  </ul>
                </div>
              </div>

              {/* Business Intelligence Section */}
              <div className="blog-section">
                <h2><TrendingUp className="section-icon" /> Business Intelligence: Real-Time Insights for Decision-Making</h2>
                
                <h3>What is Business Intelligence (BI)?</h3>
                <p>
                  BI takes reporting to the next level by providing interactive dashboards, self-service analytics, 
                  and real-time insights. Unlike static reports, BI tools allow users to explore data, identify trends, 
                  and make quick business decisions.
                </p>

                <div className="feature-card">
                  <h4>Key Characteristics:</h4>
                  <ul className="feature-list">
                    <li><CheckCircle size={18} /> <strong>Visual dashboards & charts</strong> – Easier interpretation of data</li>
                    <li><CheckCircle size={18} /> <strong>Real-time monitoring</strong> – Access to live data for quick decision-making</li>
                    <li><CheckCircle size={18} /> <strong>Data integration</strong> – Combines data from multiple sources</li>
                    <li><CheckCircle size={18} /> <strong>Self-service capabilities</strong> – Users can create their own reports</li>
                  </ul>
                </div>

                <div className="tools-grid">
                  <h4>Popular BI Tools:</h4>
                  <div className="tool-cards">
                    <div className="tool-card">
                      <strong>Power BI</strong>
                      <p>Microsoft's leading self-service BI tool</p>
                    </div>
                    <div className="tool-card">
                      <strong>Tableau</strong>
                      <p>Known for its interactive data visualizations</p>
                    </div>
                    <div className="tool-card">
                      <strong>Looker</strong>
                      <p>Google's BI platform for deep data exploration</p>
                    </div>
                    <div className="tool-card">
                      <strong>Qlik Sense</strong>
                      <p>Associative analytics for better insights</p>
                    </div>
                  </div>
                </div>

                <div className="use-case-box">
                  <h4>When to Use BI?</h4>
                  <ul>
                    <li>Sales & Marketing Performance Tracking – Identifying top-performing regions and campaigns</li>
                    <li>Financial Dashboards – Real-time KPI monitoring and budget tracking</li>
                    <li>Operations & Supply Chain Management – Real-time inventory and logistics tracking</li>
                  </ul>
                </div>
              </div>

              {/* Analytics Section */}
              <div className="blog-section">
                <h2><Brain className="section-icon" /> Analytics: The Power of Data Science & AI</h2>
                
                <h3>What is Analytics?</h3>
                <p>
                  Analytics is where businesses gain predictive and prescriptive insights using data science, 
                  machine learning (ML), and artificial intelligence (AI). It goes beyond BI by not only showing 
                  past and present trends but also forecasting future outcomes and suggesting optimal actions.
                </p>

                <div className="feature-card">
                  <h4>Key Characteristics:</h4>
                  <ul className="feature-list">
                    <li><CheckCircle size={18} /> <strong>Predictive analytics</strong> – Forecasts future trends based on historical data</li>
                    <li><CheckCircle size={18} /> <strong>Machine learning & AI-driven insights</strong> – Automates complex decision-making</li>
                    <li><CheckCircle size={18} /> <strong>Big Data Processing</strong> – Works with structured and unstructured data at scale</li>
                    <li><CheckCircle size={18} /> <strong>Custom statistical modeling</strong> – Industry-specific insights</li>
                  </ul>
                </div>

                <div className="tools-grid">
                  <h4>Popular Analytics Tools:</h4>
                  <div className="tool-cards">
                    <div className="tool-card">
                      <strong>Python & R</strong>
                      <p>Programming languages for advanced data analysis</p>
                    </div>
                    <div className="tool-card">
                      <strong>ThoughtSpot</strong>
                      <p>Search-driven analytics platform</p>
                    </div>
                    <div className="tool-card">
                      <strong>SAS, SPSS</strong>
                      <p>Statistical analysis software</p>
                    </div>
                    <div className="tool-card">
                      <strong>Alteryx & DataRobot</strong>
                      <p>No-code analytics platforms for business users</p>
                    </div>
                  </div>
                </div>

                <div className="use-case-box">
                  <h4>When to Use Analytics?</h4>
                  <ul>
                    <li>Fraud Detection & Risk Management – Spotting anomalies in transactions</li>
                    <li>Customer Churn Prediction – Identifying which customers are likely to leave</li>
                    <li>Healthcare & Diagnostics – Predicting disease outbreaks or patient outcomes</li>
                    <li>Market Trend Analysis – Understanding consumer demand and pricing strategies</li>
                  </ul>
                </div>

                <div className="limitation-box">
                  <h4>Limitations:</h4>
                  <ul>
                    <li>Requires expertise – Data scientists and specialized tools are needed</li>
                    <li>High computational cost – Processing big data and training AI models can be expensive</li>
                  </ul>
                </div>
              </div>

              
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost1;